// @flow
import React, {
    useEffect, useContext, useState, useCallback,
} from 'react';
import {
    FormattedMessage, injectIntl, IntlShape, useIntl,
} from 'react-intl';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { openPanel } from 'esm-web-pro/redux/side-panel';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { CheckboxContext } from 'esm-web-pro/containers/pages/projects/mapobjects/checkbox/CheckboxContext';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import PageMainSelector from 'esm-web-pro/components/pages/PageMainSelector';
import GroupActionsSidePanel from 'esm-web-pro/containers/pages/projects/mapobjects/sidepanel/GroupActionsSidePanel';
import { History, Match, withRouter } from 'react-router-dom';
import permissionRequired from 'esm-web-pro/decorators/permissionRequired';

type Props = {
    unlink: Function,
    intl: IntlShape,
    breadcrumb: String,
    project: Object,
    confirm: (confirmation: Confirmation) => void,
    messages: Object,
    excludeFields: Array,
    resourceName: String,
    permissions: Array,
    tableId: String,
    columns: Function,
    panelId: String,
    getObjectType: Function,
    match: Match,
    history: History,
    removeBreadcrumb: (title: string) => void,
}

const MapObjectsPage = (props: Props) => {
    const {
        unlink, intl, project, confirm, messages, excludeFields, resourceName, permissions,
        tableId, columns, panelId, getObjectType,
        removeBreadcrumb: removeBreadcrumbAction, breadcrumb,
    } = props;

    const { formatMessage } = useIntl();

    const [choiceDisabled, setChoiceDisabled] = useState(true);

    const [label, setLabel] = useState(null);

    const {
        selected, setSelected, onSelectAll, selectAll,
    } = useContext(CheckboxContext);

    const dispatch = useDispatch();

    const listRef = React.useRef(null);

    useEffect(() => {
        if (project) {
            dispatch(addBreadcrumbs([
                breadcrumb(project),
            ]));
        }
        return () => {
            if (project) {
                dispatch(removeBreadcrumb(breadcrumb(project)));
            }
        };
    }, [removeBreadcrumbAction, dispatch, project, breadcrumb]);

    const reloadData = () => {
        if (listRef.current && listRef.current.table.current) {
            listRef.current.table.current.reloadData();
        }
    };

    // Reset state of CheckboxContext
    useEffect(() => {
        setSelected({});
        onSelectAll(false);
    }, [resourceName]);

    useEffect(() => {
        if (isEmpty(selected)) {
            setChoiceDisabled(true);
        } else {
            setChoiceDisabled(false);
        }
    }, [selected]);

    const invokeConfirmDialog = useCallback((message, confirmCallback) => confirm({
        message,
        actions: {
            cancel: {
                label: <FormattedMessage id="cancel-btn" defaultMessage="Cancel" />,
                callback: () => {
                },
                type: 'negative',
            },
            confirm: {
                label: <FormattedMessage id="confirm-btn" defaultMessage="Confirm" />,
                callback: confirmCallback,
                type: 'positive',
            },
        },
    }), [confirm]);

    const unlinkMultipleObjects = () => {
        const objectsArray = Object.values(selected);
        const data = objectsArray.map((object) => object.original.uuid);
        const type = getObjectType(objectsArray[0].original);
        unlink({ uuids: [...data], type })
            .then(() => {
                setSelected({});
                onSelectAll(false);
                reloadData();
            })
            .catch((err) => console.log(err));
    };

    const permissionsPageMainSelector = [...permissions.actions.change, ...permissions.actions.delete];
    const PermissionRequiredPageMainSelector = permissionRequired(permissionsPageMainSelector)(PageMainSelector);

    const choices = [
        {
            disabled: choiceDisabled,
            label: <FormattedMessage id="group-actions-change-stage" defaultMessage="Change Stage" />,
            onClick: () => {
                setLabel(choices[0].label);
                dispatch(openPanel(
                    'GISPOINT_GROUPACTIONS_SIDEPANEL',
                    <FormattedMessage id="map-objects-group-actions" defaultMessage="Group actions" />,
                    null,
                    true,
                ));
            },
        },
        {
            disabled: choiceDisabled,
            label: <FormattedMessage id="group-actions-unlink" defaultMessage="Unlink" />,
            onClick: () => invokeConfirmDialog(intl.formatMessage(messages.unlinkObjects), unlinkMultipleObjects),
        },

    ];

    return (
        <>
            <GroupActionsSidePanel
                getObjectType={getObjectType}
                resourceArguments={[project.uuid]}
                project={project.uuid}
                resourceName={resourceName}
                excludeFields={excludeFields}
                permissions={permissions}
                reload={reloadData}
                editorTitle={(
                    <FormattedMessage
                        id="project-edit-object"
                        defaultMessage="Edit object"
                    />
                )}
            />
            <ListPage
                ref={listRef}
                {...props}
                headerButtons={[<PermissionRequiredPageMainSelector label={label} choices={choices} />]}
                resourceName={resourceName}
                resourceArguments={[project.uuid]}
                permissions={permissions.view}
                tableId={tableId}
                columns={columns(onSelectAll, selectAll, project, panelId, reloadData)}
            />
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    unlink: {
        send: (data: array) => apiClient.resources.groupActions.unlink(data),
    },
});

export default connectAPI(apiCallback)(injectIntl(withConfirmation(withRouter(MapObjectsPage))));
